import { attributes, AttributesMixin, define, Model, type } from "@type-r/models";
import { isEmpty } from "lodash";
import { poll } from "../shared";
import { AssetTypeItem } from "./assetTypes";
import { getAuthorizationBearer, getPlatformCode, restfulIO } from "./endpoint";

const PartialIngestionJob = attributes({
    status: String
});

export interface IngestionJob extends AttributesMixin<typeof IngestionJob> { }
@define
export class IngestionJob extends Model {
    static endpoint = restfulIO('api/v1/ingest/jobs');

    static attributes = {
        status: String,
        fileName: String,
        assetType: Number,
        importJobId: Number,
        invalid: type(PartialIngestionJob).dontSave.endpoint(restfulIO("./invalid")),
        valid: type(PartialIngestionJob).dontSave.endpoint(restfulIO("./valid")),
        modifyExistingAssets: type(PartialIngestionJob).dontSave.endpoint(restfulIO("./modifyExistingAssets")),
    }

    hasErrors() {
        return this.status === 'parsererror';
    }

    reset() {
        this.id = null;
        this.status = null;
    }

    async waitForEnqueueToBeDone() {
        await poll(async () => await this.fetch({ params: { id: this.id } }), () => this.status != 'validated' && !this.hasErrors(), 3000);
        return this;
    }

    appendFromDataCriteria({ formData, items }: { formData: FormData, items: { assetType: AssetTypeItem, searchValue: string }[] }) {
        items.forEach((item, index) => (
            formData.append(`assetFilterCriteria.criteria[${index}].assetType.id`, item.assetType.id),
            formData.append(`assetFilterCriteria.criteria[${index}].assetType.name`, item.assetType.name),
            formData.append(`assetFilterCriteria.criteria[${index}].searchValue`, item.searchValue)
        ));
    }

    appendFromDataAssetFilterCriteria({ formData, item }: { formData: FormData, item }) {
        if (!isEmpty(item)) {
            formData.append('assetFilterCriteria.filterColumn.name', item.filterColumn.name);
            formData.append('assetFilterCriteria.filterColumn.x', item.filterColumn.x);
            formData.append('assetFilterCriteria.filterColumn.y', item.filterColumn.y);
            this.appendFromDataCriteria({ formData: formData, items: item.criteria });
        }
    }

    async enqueue(options: { params: { file, sourceId, sourceName, fileCode, userTemplate?, userTemplateId?, dataHolderId?, staticFields?, omitUnmappedFields?, assetTypes?, assetFilterCriteria?, workSheet?, modifyExistingAssets?, mergeExistingAssets?} }) {
        const { file, sourceId, sourceName, fileCode, userTemplate, userTemplateId, dataHolderId, staticFields, omitUnmappedFields, assetTypes, assetFilterCriteria, workSheet, modifyExistingAssets, mergeExistingAssets } = options.params;
        const url = (this.getEndpoint() as any).getUrl(this);
        const formData = new FormData();
        formData.append('file', file, file.name);
        formData.append('sourceId', sourceId);
        formData.append('fileCode', fileCode);
        formData.append('sourceName', sourceName);

        if (dataHolderId) {
            formData.append('dataHolderId', dataHolderId);
        }
        if (userTemplateId) {
            formData.append('userTemplateId', userTemplateId);
        }
        if (userTemplate) {
            formData.append('userTemplate', userTemplate);
        }
        if (staticFields) {
            formData.append('staticFields', staticFields);
        }
        if (omitUnmappedFields) {
            formData.append('omitUnmappedFields', omitUnmappedFields);
        }
        if (assetTypes) {
            formData.append('assetTypes', assetTypes);
        }
        if (assetFilterCriteria && assetFilterCriteria.filterColumn) {
            this.appendFromDataAssetFilterCriteria({ formData: formData, item: assetFilterCriteria });
        }
        if (workSheet) {
            formData.append('workSheet.id', workSheet.id);
            formData.append('workSheet.name', workSheet.name);
        }
        if (modifyExistingAssets) {
            formData.append('modifyExistingAssets', modifyExistingAssets)
        }
        if (mergeExistingAssets) {
            formData.append('mergeExistingAssets', mergeExistingAssets)
        }

        const response = await fetch(url, {
            method: 'POST',

            headers: new Headers({
                "Authorization": getAuthorizationBearer(),
                'X-Platform-Code': getPlatformCode()
            }),
            body: formData
        });


        if (response.status < 200 || response.status >= 300) {
            throw new Error(response.statusText || `HTTP Error ${response.status}`);
        }

        const json = await response.json();
        this.set(json);
        return this;
    }

    async saveValid(params) {
        this.valid.id = null;
        await this.valid.save(params);
    }

    async saveInvalid(params) {
        this.invalid.id = null;
        await this.invalid.save(params);
    }

    async runModifyExistingAssets(params) {
        this.modifyExistingAssets.id = null;
        await this.modifyExistingAssets.save(params);
    }

    async doDeleteAllAssets(id: number, assetType: Number) {
        const url = `api/v1/ingest/jobs/${id}/assets/delete-all?assetTypeId=${assetType}`;
        return await this.delete(url);
    }

    async doDeleteBulkAssets(ids: number[]) {
        const url = `api/v1/ingest/jobs/assets/delete-bulk`;
        return await this.delete(url, ids);
    }

    async delete(url, payload = {}) {
        const response = await fetch(url, {
            method: 'DELETE',
            headers: new Headers({
                'Authorization': getAuthorizationBearer(),
                'Content-Type': 'application/json',
                'X-Platform-Code': getPlatformCode()
            }),
            body: JSON.stringify(payload)
        });
        if (response.status < 200 || response.status >= 300) {
            let error = response.statusText || `HTTP Error ${response.status}`;
            await response.json()
                .then(e => error = e.error);
            throw new Error(error);
        }
        return await response.json();
    }
}
